import envPublic from '@/config/env.public';
import singletonRouter from 'next/router';
import { createInstantSearchRouterNext } from 'react-instantsearch-router-nextjs';

/**
 * Creates a custom InstantSearch Router for Next.js using Algolia.
 *
 * @param {string | undefined} serverUrl - Optional server URL to use. If an empty string is passed, it will be undefined.
 * @param {string} [customBaseUrl] - Optional custom base URL.
 * @param {string} [countrySlug] - Optional country slug to include in the URL.
 * @returns {object} Configured InstantSearch router.
 */
export default function createInstantSearchRouterNextUtil(
  serverUrl?: string,
  customBaseUrl?: string,
  countrySlug?: string,
  locale?: string
) {
  return createInstantSearchRouterNext({
    serverUrl: serverUrl === '' ? undefined : serverUrl,
    singletonRouter,
    beforePopState: ({ state, ownBeforePopState }) => {
      // Execute the router's own beforePopState logic
      return ownBeforePopState(state);
    },
    routerOptions: {
      cleanUrlOnDispose: false,
      createURL({ qsModule, location, routeState }) {
        // Determine base URL: use customBaseUrl if provided, else use the location origin
        const baseUrl = customBaseUrl ?? location.origin;
        const pathParts = location.pathname?.split('/');
        // Assuming a single index key exists in routeState
        const indexName = Object.keys(routeState)[0];
        const currentState = routeState[indexName] || {};

        // Extract state objects for refinements, menu, toggles, range, etc.
        const currentRefinements = currentState.refinementList || {};
        const currentMenu = currentState.menu || {};
        const currentToggle = currentState.toggle || {};
        const currentRange = currentState.range || {};
        const currentQuery = currentState.query || '';
        const currentSortBy = currentState.sortBy || '';
        const currentPage = currentState.page || 1;

        // Helper functions to check for refinements
        const hasRefinement = (key: string) => key in currentRefinements;
        const shouldRemoveRefinement = (key: string, pathSegment: string) =>
          !hasRefinement(key) && location.pathname?.includes(pathSegment);

        // Determine which refinements are present
        const hasBrandRefinement = hasRefinement('brand.name');
        const hasSubCategoryRefinement = hasRefinement('subCategory');
        const hasCountryRefinement = hasRefinement('brand.countryOfOrigin');
        const hasRegionRefinement = hasRefinement('brand.region');
        const hasCategoryMenu = 'category.name' in currentMenu;
        const hasAskToggle = 'hasAsk' in currentToggle;
        const hasBidToggle = 'hasBid' in currentToggle;
        const hasLowestAskRange = 'lowestAsk' in currentRange;
        const hasQuery = Boolean(currentQuery);
        const hasSortBy = Boolean(currentSortBy);

        // Check if certain refinements should be removed based on URL path
        const shouldRemoveBrand = shouldRemoveRefinement('brand.name', 'brands');
        const shouldRemoveSubCategory = shouldRemoveRefinement('subCategory', 'subcategories');
        const shouldRemoveCountry = shouldRemoveRefinement('brand.countryOfOrigin', 'country');
        const shouldRemoveRegion = shouldRemoveRefinement('brand.region', 'region');
        const shouldRemoveCategoryMenu =
          !hasCategoryMenu && location.pathname?.includes('category');

        /**
         * Constructs a query object excluding a specific refinement key.
         *
         * @param {string} excludeKey - The refinement key to exclude.
         * @returns {Record<string, any>} Query object.
         */
        function getQueryWithoutRefinement(excludeKey: string): Record<string, any> {
          const query: Record<string, any> = {};

          if (hasBrandRefinement && excludeKey !== 'brand.name') {
            query['brand.name'] = currentRefinements['brand.name']
              .map((name: string) => name.trim().replace(/\s+/g, '-'))
              .join('+');
          }
          if (hasSubCategoryRefinement && excludeKey !== 'subCategory') {
            query['subCategory'] = currentRefinements['subCategory']
              .map((s: string) => s.trim().replace(/\s+/g, '-'))
              .join('+');
          }
          if (hasCountryRefinement && excludeKey !== 'brand.countryOfOrigin') {
            query['brand.countryOfOrigin'] = currentRefinements['brand.countryOfOrigin']
              .map((c: string) => c.trim().replace(/\s+/g, '-'))
              .join('+');
          }
          if (hasRegionRefinement && excludeKey !== 'brand.region') {
            query['brand.region'] = currentRefinements['brand.region']
              .map((r: string) => r.trim().replace(/\s+/g, '-'))
              .join('+');
          }
          if (hasAskToggle) {
            query['hasAsk'] = currentToggle['hasAsk'];
          }
          if (hasBidToggle) {
            query['hasBid'] = currentToggle['hasBid'];
          }
          if (hasLowestAskRange) {
            const lowestAskValue = currentRange['lowestAsk'];
            if (lowestAskValue) {
              const [min, max] = lowestAskValue.split(':');
              if (min) query['min'] = min;
              if (max) query['max'] = max;
            }
          }
          if (hasQuery) {
            query['search'] = currentQuery.trim().replace(/\s+/g, '-');
          }
          if (hasSortBy) {
            query['sortBy'] = currentSortBy;

            // Si acaban de elegir la réplica de release-calendar, fuerza el filtro
            if (currentSortBy === envPublic.NEXT_PUBLIC_ALGOLIA_REPLICA_BY_RELEASE_CALENDAR) {
              // Algolia espera filters=isUpcoming=1
              query['filters'] = 'isUpcoming=1';
            }
          }
          return query;
        }

        /**
         * Constructs a URL based on the route type, slug, and additional search parameters.
         *
         * @param {('brands' | 'subcategories' | 'category' | 'country' | 'region' | 'search')} type - Type of route.
         * @param {string} slug - The slug value to include in the URL.
         * @param {Record<string, any>} [searchParams] - Additional search parameters.
         * @returns {string} Constructed URL.
         */
        function constructUrl(
          type: 'brands' | 'subcategories' | 'category' | 'country' | 'region' | 'search',
          slug: string,
          searchParams?: Record<string, any>
        ): string {
          // Remove indexName from searchParams to avoid duplication
          if (searchParams) {
            delete searchParams[indexName];
          }
          // Build the query string including pagination if currentPage is not 1
          const qs = qsModule.stringify(searchParams);
          const queryString = qs
            ? currentPage !== 1
              ? `?${qs}&page=${currentPage}`
              : `?${qs}`
            : currentPage !== 1
              ? `?page=${currentPage}`
              : '';

          // If a category menu exists, add it as a prefix in the URL
          const categorySlug = hasCategoryMenu ? `/category/${currentMenu['category.name']}` : '';

          if (type === 'category') {
            return `${baseUrl}${categorySlug}${queryString}`;
          }
          if (type === 'search') {
            return `${baseUrl}${categorySlug}/search?search=${slug}`;
          }
          // For other types, ensure the slug is sanitized
          return `${baseUrl}${categorySlug}/${type}/${slug.trim().replace(/\s+/g, '-')}${queryString}`;
        }

        if (hasCountryRefinement) {
          return constructUrl(
            'country',
            currentRefinements['brand.countryOfOrigin'].join('+'),
            getQueryWithoutRefinement('brand.countryOfOrigin')
          );
        }
        // ---------------------

        // Continue with other conditions if no country refinement is detected.
        if (hasBrandRefinement) {
          const brands = currentRefinements['brand.name'];
          return constructUrl('brands', brands.join('+'), getQueryWithoutRefinement('brand.name'));
        }
        if (hasSubCategoryRefinement) {
          const subCategories = currentRefinements['subCategory'];
          return constructUrl(
            'subcategories',
            subCategories.join('+'),
            getQueryWithoutRefinement('subCategory')
          );
        }
        if (hasCategoryMenu) {
          return constructUrl(
            'category',
            currentMenu['category.name'],
            getQueryWithoutRefinement('category.name')
          );
        }
        if (hasRegionRefinement) {
          const regionValues = currentRefinements['brand.region'];
          return constructUrl(
            'region',
            regionValues.join('+'),
            getQueryWithoutRefinement('brand.region')
          );
        }
        if (hasQuery) {
          return constructUrl('search', currentQuery, getQueryWithoutRefinement('search'));
        }
        // Fallback: return a basic search URL if any refinements or menus need to be removed
        const basicParams = qsModule.stringify(getQueryWithoutRefinement('none'));
        if (
          shouldRemoveBrand ||
          shouldRemoveSubCategory ||
          shouldRemoveCategoryMenu ||
          shouldRemoveCountry ||
          shouldRemoveRegion
        ) {
          return `${baseUrl}/search${basicParams ? `?${basicParams}` : ''}`;
        }
        return `${baseUrl}/search${basicParams ? `?${basicParams}` : ''}`;
      },
      parseURL({ qsModule, location }) {
        const pathParts = location.pathname?.split('/');
        // Initialize refinements, menu, toggles, and range with default values
        const refinements: Record<string, any> = {
          'brand.countryOfOrigin': [],
          'brand.name': [],
          subCategory: [],
          'category.name': [],
          'brand.region': [],
          search: [],
          hasAsk: false,
          hasBid: false,
          min: '',
          max: '',
          sortBy: '',
          page: 1,
        };
        const menu: Record<string, string> = {};
        const toggle: Record<string, boolean> = {};
        const range: Record<string, string> = {};

        /**
         * Helper to extract items from the URL path after a given segment.
         *
         * @param {string} segment - The segment after which to extract items.
         * @returns {string[]} Array of extracted items.
         */
        const getItemsFromPath = (segment: string): string[] => {
          const index = pathParts.indexOf(segment);
          if (index !== -1 && pathParts[index + 1]) {
            return pathParts
              .slice(index + 1)
              .map(item => decodeURI(item).replace(/-/g, ' '))
              .join(',')
              .split('+');
          }
          return [];
        };

        // Extract items for each segment
        const brandItems = getItemsFromPath('brands');
        const subCategoryItems = getItemsFromPath('subcategories');
        const categoryItems = getItemsFromPath('category');
        const countryItems = getItemsFromPath('country');
        const regionItems = getItemsFromPath('region');

        // Handle country prefix from URL if it follows the format "country-<value>"
        const countryFromPath = pathParts.length > 1 ? pathParts[1] : '';
        if (countryFromPath && countryFromPath.includes('country-')) {
          const parts = countryFromPath.split('-');
          if (parts[1]) {
            refinements['brand.countryOfOrigin'].push(parts[1]);
          }
        }
        // If a countrySlug is provided, add it as a refinement
        if (countrySlug) {
          refinements['brand.countryOfOrigin'].push(countrySlug);
        }

        // Map extracted URL path items into the corresponding refinements and menu
        if (brandItems.length) {
          refinements['brand.name'].push(...brandItems);
        }
        if (subCategoryItems.length) {
          refinements.subCategory.push(...subCategoryItems);
        }
        if (categoryItems.length) {
          menu['category.name'] = categoryItems[0];
        }
        if (countryItems.length) {
          refinements['brand.countryOfOrigin'].push(...countryItems);
        }
        if (regionItems.length) {
          refinements['brand.region'].push(...regionItems);
        }

        // Parse query parameters from the URL search string
        const queryParams = qsModule.parse(location.search.replace('?', ''));
        const pageParam = Number(queryParams.page);
        refinements.page = pageParam > 0 ? pageParam : 1;

        const filtersParam = queryParams.filters?.toString();

        /**
         * Adds search parameter refinements to a target array.
         *
         * @param {string} param - The query parameter name.
         * @param {string[]} targetArray - Array where the values will be added.
         */
        function addSearchParamRefinements(param: string, targetArray: string[]) {
          const searchParam = queryParams[param];
          if (searchParam) {
            targetArray.push(
              ...searchParam
                .toString()
                .split('+')
                .map(val => val.replace(/-/g, ' '))
            );
          }
        }

        // Process query parameters for refinements
        addSearchParamRefinements('subCategory', refinements.subCategory);
        addSearchParamRefinements('brand.name', refinements['brand.name']);
        addSearchParamRefinements('brand.countryOfOrigin', refinements['brand.countryOfOrigin']);
        addSearchParamRefinements('brand.region', refinements['brand.region']);

        // Process category menu from query if available
        if (queryParams['category.name']) {
          menu['category.name'] = queryParams['category.name'].toString().replace(/-/g, ' ');
        }

        // Process toggles from query parameters
        toggle.hasAsk = Boolean(queryParams.hasAsk);
        toggle.hasBid = Boolean(queryParams.hasBid);

        // Process price range if provided
        if (queryParams.min || queryParams.max) {
          range.lowestAsk = `${queryParams.min || ''}:${queryParams.max || ''}`;
        }

        // Merge parsed query parameters into the final search state using the defined index key
        const parsedQuery = qsModule.parse(location.toString().split('?')[1]) as Record<
          string,
          any
        >;

        const indexKey = envPublic.NEXT_PUBLIC_ALGOLIA_INDEX;

        if (filtersParam) {
          // Ensure the indexKey object exists before setting filters
          const indexKeyObject = parsedQuery[`${indexKey}`];
          if (!indexKeyObject) {
            parsedQuery[`${indexKey}`] = {};
          }

          parsedQuery[`${indexKey}`].filters = filtersParam;
        }

        return {
          [indexKey]: {
            ...(parsedQuery[`${indexKey}`] || {}),
            refinementList: {
              ...(parsedQuery[`${indexKey}`]?.refinementList || {}),
              ...refinements,
            },
            menu: {
              ...(parsedQuery[`${indexKey}`]?.menu || {}),
              ...menu,
            },
            toggle: {
              ...(parsedQuery[`${indexKey}`]?.toggle || {}),
              ...toggle,
            },
            range: {
              ...(parsedQuery[`${indexKey}`]?.range || {}),
              ...range,
            },
            query: queryParams.search?.toString().replace(/-/g, ' ') || '',
            page: refinements.page,
            sortBy: queryParams.sortBy || '',
          },
        };
      },
    },
  });
}
